/**
 * A legal document, readable without an account.
 *
 * Deliberately not behind the auth gate: terms you have to sign up to read are
 * terms nobody read, and a property owner who wants to know what subscribers
 * are forbidden to do should not have to become one.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { LegalDocument, LegalSlug } from "../api/types";
import { Link } from "../router";
import { Markdown } from "./Markdown";

export const LEGAL_ROUTES: Record<string, LegalSlug> = {
  "/terms": "terms",
  "/privacy": "privacy",
  "/acceptable-use": "acceptable-use",
  "/data-licence": "data-licence",
};

export const LEGAL_NAV: Array<{ path: string; label: string }> = [
  { path: "/terms", label: "Terms" },
  { path: "/privacy", label: "Privacy" },
  { path: "/acceptable-use", label: "Acceptable use" },
  { path: "/data-licence", label: "Data licence" },
];

export function LegalPage({ slug }: { slug: LegalSlug }) {
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setDocument(null);
    setError("");
    api
      .legalDocument(slug, controller.signal)
      .then(setDocument)
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(
          cause instanceof ApiError ? cause.message : "Couldn't load this page.",
        );
      });
    return () => controller.abort();
  }, [slug]);

  return (
    <>
      <header className="topbar">
        <Link to="/" className="wordmark">
          <span>D</span>is<span>TR</span>ess Signal
        </Link>
        <div className="loc hide-sm">Legal</div>
        <div className="spacer" />
        <Link to="/" className="linkbtn">
          Back to the app
        </Link>
      </header>

      <article className="legal">
        <nav className="legal-nav" aria-label="Legal documents">
          {LEGAL_NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={
                LEGAL_ROUTES[item.path] === slug ? "legal-tab current" : "legal-tab"
              }
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {error ? (
          <div className="err">{error}</div>
        ) : !document ? (
          <p className="sub">Loading…</p>
        ) : (
          <>
            <div className="eyebrow">
              Version {document.version} · effective {document.effective}
            </div>
            <Markdown source={document.body} />
          </>
        )}
      </article>
    </>
  );
}
